import React, { useEffect, useState } from 'react';
import Cognito from '../../utils/Cognito';
import { DimmerLoader } from '../../../../components/Loaders';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import {
  AuthFieldContainer,
  AuthFieldLabel,
  AuthFormContainer,
  AuthInputField,
  AuthSubmitButton,
} from '../LoginPagesUI';
import styled from 'styled-components';
import FieldError from '../../../../components/Form/FieldError';
import { TOAST_ERROR_MESSAGE } from '../../utils/utils';
import { AxiosError } from 'axios';
import { ICognitoResponse, ILoginInput, LoginStep } from '../types';
import { Updater } from 'use-immer';
import { goBackToLoginStep } from './utils';
import toastMessage from '../../../../utils/toastMessage';
import { theme } from '../../../../styles/theme';
import { SecondaryButton } from '../../../../components/Button';
import KubitTooltip from '../../../../components/KubitTooltip';
import { assertNotNil } from '../../../../utils/types';

interface IProps {
  email: string;
  setLoginStep: (step: LoginStep) => void;
  setLoginInput: Updater<ILoginInput>;
}

const ContextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  row-gap: 8px;
`;
const ContextTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.secondary900};
`;
const ContextDescription = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${theme.colors.secondary800};
`;
const QRCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 16px;
`;
const CopySecretCodeButton = styled(SecondaryButton)`
  width: 100%;
  min-height: 52px;
  margin-top: 8px;
  font-size: 16px;
`;
const DisplayQRCodeAction = styled.button`
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  padding: 0;
  border: 0;
  outline: none;
  color: ${theme.colors.secondary600};
  &:hover {
    cursor: pointer;
    color: ${theme.colors.mainBlue};
  }
`;

const QRTextDictionary = {
  code: {
    title: 'Step 1: Scan this QR code',
    description:
      'Scan the following QR code with your authenticator app, such as Google' +
      ' Authenticator, Duo Mobile, Authy, etc.',
  },
  text: {
    title: 'Step 1: Copy and paste the key',
    description:
      'Paste below key to your authenticator app, like Google Authenticator, Duo mobile, Authy, etc.',
  },
};
const MfaSetupForm = ({ email, setLoginStep, setLoginInput }: IProps) => {
  const navigate = useNavigate();
  const [secretCode, setSecretCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyToken, setVerifyToken] = useState('');
  const [isDisplayQRCode, setIsDisplayQRCode] = useState(true);
  const [hasError, setHasError] = useState(false);
  const onSetupMfa = (e: React.MouseEvent) => {
    e.preventDefault();
    setLoading(true);
    Cognito.confirmMfa(verifyToken)
      .then(() => {
        navigate('/');
      })
      .catch((err: AxiosError<ICognitoResponse>) => {
        setLoading(false);
        const status = err.response?.data?.status;
        switch (status) {
          case 400:
            setHasError(true);
            return;
          case 401:
            toastMessage({
              message:
                'Your session has expired. Redirecting to the login page.',
              type: 'info',
            });
            goBackToLoginStep({ setLoginStep, setLoginInput });
            return;
          default:
            toastMessage({ message: TOAST_ERROR_MESSAGE, type: 'error' });
            console.error('Unexpected error on verify token.');
            goBackToLoginStep({ setLoginStep, setLoginInput });
        }
      });
  };

  const onCopySecretCode = async () => {
    await navigator.clipboard.writeText(secretCode);
    toastMessage({ message: 'Code Copied!', type: 'info' });
  };

  const getQRCodeValue = () => {
    return `otpauth://totp/${email}?secret=${secretCode}&issuer=Kubit`;
  };

  const onChangeTokenInput = (value: string) => {
    if (hasError) {
      setHasError(false);
    }
    setVerifyToken(value);
  };
  useEffect(() => {
    setLoading(true);
    // fetch the qr code

    Cognito.fetchMfaSecretCode()
      .then((res) => {
        const secretCode = res.data.secretCode;
        assertNotNil(secretCode);
        setSecretCode(secretCode);
      })
      .catch((error: AxiosError<ICognitoResponse>) => {
        // handle error
        toastMessage({
          message: TOAST_ERROR_MESSAGE,
          type: 'error',
        });
        console.error('Failed on fetching QR secret code.', error);
        navigate('/');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate]);

  if (secretCode.length === 0) {
    return <DimmerLoader />;
  }

  return (
    <AuthFormContainer>
      <ContextContainer>
        <ContextTitle>
          {isDisplayQRCode
            ? QRTextDictionary.code.title
            : QRTextDictionary.text.title}
        </ContextTitle>
        <ContextDescription>
          {isDisplayQRCode
            ? QRTextDictionary.code.description
            : QRTextDictionary.text.description}
        </ContextDescription>
      </ContextContainer>

      {isDisplayQRCode ? (
        <QRCodeContainer>
          <QRCodeCanvas value={getQRCodeValue()} />
          <DisplayQRCodeAction
            type="button"
            onClick={() => setIsDisplayQRCode(false)}
          >
            Can’t scan the QR code?
          </DisplayQRCodeAction>
        </QRCodeContainer>
      ) : (
        <QRCodeContainer>
          <KubitTooltip content={'Copy setup key'} placement="top">
            <CopySecretCodeButton
              type="button"
              isOutlined={true}
              onClick={onCopySecretCode}
              trackContext="login"
              trackAction="mfa setup - copy setup key"
            >
              {secretCode}
            </CopySecretCodeButton>
          </KubitTooltip>
          <DisplayQRCodeAction
            type="button"
            onClick={() => setIsDisplayQRCode(true)}
          >
            Want to scan QR code?
          </DisplayQRCodeAction>
        </QRCodeContainer>
      )}

      <ContextContainer>
        <ContextTitle>Step 2: Enter the one-time code</ContextTitle>
        <ContextDescription>
          Enter the 6-digit verification code generated by the authenticator
          app.
        </ContextDescription>
      </ContextContainer>
      <AuthFieldContainer>
        <AuthFieldLabel>Verification code</AuthFieldLabel>
        <AuthInputField
          placeholder={'- - - - - -'}
          value={verifyToken}
          onChange={onChangeTokenInput}
          autoComplete={'off'}
          type="text"
        />
        {hasError && (
          <FieldError
            type="custom"
            customError={'Token is not correct, please try again.'}
          />
        )}
      </AuthFieldContainer>
      <AuthSubmitButton
        type="submit"
        loading={loading}
        onClick={onSetupMfa}
        trackContext="login"
        trackAction="mfa setup - confirm"
      >
        Confirm
      </AuthSubmitButton>
    </AuthFormContainer>
  );
};

export default MfaSetupForm;
